import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  FileText,
  Globe,
  Linkedin,
  Check,
  Loader2,
  Search,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { companySearchApi } from "@/api/company-search-api";
import { getLinkedInUrlValidationError, getUrlValidationError } from "@/lib/url-utils";
import { cn } from "@/lib/utils";
import { StepCard, StepCardLabel } from "../step-card";
import { motionSafe, rowVariants, staggerContainer, SPRING } from "../motion";

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

export interface DealDraft {
  companyName: string;
  website: string;
  linkedIn: string;
  file: File | null;
}

interface Suggestion {
  url: string;
  title?: string;
  description?: string;
  favicon?: string;
}

/**
 * The search service returns either a flat suggestion array or an array of
 * `{ field_name, suggestions }` wrappers. Same normalisation as the Add Deal
 * dialog — kept local rather than shared because that component owns a much
 * larger surface and isn't worth coupling to.
 */
interface SuggestionGroup {
  field_name?: string;
  suggestions?: Suggestion[];
}

function extractSuggestions(response: unknown, fieldName: string): Suggestion[] {
  // The endpoint's return type is `unknown` at the API boundary; narrow once
  // here rather than threading casts through every caller.
  const envelope = response as
    | { results?: unknown; data?: { results?: unknown } }
    | null
    | undefined;

  const results = envelope?.results ?? envelope?.data?.results;
  if (!Array.isArray(results) || results.length === 0) return [];

  const first = results[0] as (Suggestion & SuggestionGroup) | undefined;

  if (typeof first?.url === "string") return results as Suggestion[];

  if (Array.isArray(first?.suggestions)) {
    const groups = results as SuggestionGroup[];
    const match = groups.find((g) => g?.field_name === fieldName);
    if (match?.suggestions) return match.suggestions;
    return groups.flatMap((g) => g?.suggestions ?? []);
  }

  return [];
}

/**
 * Step 4 — add the first real deal.
 *
 * Typing a company name kicks off web and LinkedIn lookups in the background,
 * so by the time the user has finished the name the links are usually already
 * there to accept. That "it already knew" moment is the point of this step.
 */
export function DealStep({
  onSubmit,
  onSkip,
  isSubmitting,
  progress,
  progressLabel,
}: {
  onSubmit: (draft: DealDraft) => void;
  onSkip: () => void;
  isSubmitting: boolean;
  progress: number;
  progressLabel: string;
}) {
  const shouldReduce = useReducedMotion();

  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [linkedIn, setLinkedIn] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [linkedInSuggestions, setLinkedInSuggestions] = useState<Suggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestId = useRef(0);

  const websiteError = website ? getUrlValidationError(website) : null;
  const linkedInError = linkedIn ? getLinkedInUrlValidationError(linkedIn) : null;

  const search = useCallback(async (term: string) => {
    // Late responses from an earlier keystroke must not overwrite newer
    // results, so each run claims a ticket and checks it before committing.
    const ticket = ++requestId.current;
    setIsSearching(true);

    try {
      const [web, li] = await Promise.allSettled([
        companySearchApi.searchWeb(term),
        companySearchApi.searchLinkedIn(term),
      ]);

      if (ticket !== requestId.current) return;

      if (web.status === "fulfilled") {
        setSuggestions(
          extractSuggestions(web.value, "website")
            .filter((s) => s.url && !s.url.includes("linkedin.com"))
            .slice(0, 4)
        );
      }
      if (li.status === "fulfilled") {
        setLinkedInSuggestions(
          extractSuggestions(li.value, "linkedIn")
            .filter((s) => s.url?.includes("linkedin.com"))
            .slice(0, 3)
        );
      }
    } finally {
      if (ticket === requestId.current) setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (companyName.trim().length < 2 || (website && linkedIn)) {
      setSuggestions([]);
      setLinkedInSuggestions([]);
      return;
    }

    searchTimeout.current = setTimeout(() => search(companyName.trim()), 500);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [companyName, website, linkedIn, search]);

  const pickFile = (selected: File | undefined) => {
    if (!selected) return;
    if (selected.size > MAX_FILE_SIZE_BYTES) {
      setFileError("That file is over 25MB — try a smaller one.");
      return;
    }
    setFileError(null);
    setFile(selected);
  };

  // Once both links are filled there's nothing left to suggest.
  const showSuggestions =
    (isSearching || suggestions.length > 0 || linkedInSuggestions.length > 0) &&
    !(website && linkedIn);

  const canSubmit =
    companyName.trim().length > 0 && !websiteError && !linkedInError && !isSubmitting;

  return (
    <StepCard>
      <div className="space-y-4">
        <div>
          <StepCardLabel>Company</StepCardLabel>
          <Input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Start typing a company name…"
            className="h-11 text-[15px]"
            autoFocus
            disabled={isSubmitting}
          />
          <DealPreview
            hasName={companyName.trim().length > 0}
            hasWebsite={!!website && !websiteError}
            hasLinkedIn={!!linkedIn && !linkedInError}
            isSubmitting={isSubmitting}
          />
        </div>

        <AnimatePresence>
          {showSuggestions && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: shouldReduce ? 0 : 0.22 }}
              className="overflow-hidden"
            >
              <div className="rounded-xl border border-border/70 bg-muted/30 p-2">
                <div className="mb-1.5 flex items-center gap-1.5 px-1">
                  <motion.span
                    animate={
                      isSearching && !shouldReduce
                        ? { rotate: 360 }
                        : { rotate: 0 }
                    }
                    transition={
                      isSearching
                        ? { duration: 1.6, repeat: Infinity, ease: "linear" }
                        : SPRING
                    }
                  >
                    <Search className="h-3 w-3 text-primary" />
                  </motion.span>
                  <span className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
                    {isSearching ? "Looking it up…" : "Found these"}
                  </span>
                </div>

                <motion.div
                  className="space-y-0.5"
                  variants={motionSafe(staggerContainer, shouldReduce)}
                  initial="hidden"
                  animate="visible"
                >
                  <AnimatePresence mode="popLayout">
                    {suggestions.map((s) => (
                      <SuggestionRow
                        key={s.url}
                        suggestion={s}
                        icon={<Globe className="h-3.5 w-3.5 text-blue-500" />}
                        shouldReduce={shouldReduce}
                        onSelect={() => {
                          setWebsite(s.url);
                          if (s.title && !companyName.trim()) setCompanyName(s.title);
                          setSuggestions([]);
                        }}
                      />
                    ))}
                    {linkedInSuggestions.map((s) => (
                      <SuggestionRow
                        key={s.url}
                        suggestion={s}
                        icon={<Linkedin className="h-3.5 w-3.5 text-blue-600" />}
                        shouldReduce={shouldReduce}
                        onSelect={() => {
                          setLinkedIn(s.url);
                          setLinkedInSuggestions([]);
                        }}
                      />
                    ))}
                  </AnimatePresence>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <StepCardLabel>Website</StepCardLabel>
            <Input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://"
              disabled={isSubmitting}
              className={cn("h-9 text-sm", websiteError && "border-destructive")}
            />
            {websiteError && (
              <p className="mt-1 text-xs text-destructive">{websiteError}</p>
            )}
          </div>
          <div>
            <StepCardLabel>LinkedIn</StepCardLabel>
            <Input
              value={linkedIn}
              onChange={(e) => setLinkedIn(e.target.value)}
              placeholder="https://linkedin.com/company/…"
              disabled={isSubmitting}
              className={cn("h-9 text-sm", linkedInError && "border-destructive")}
            />
            {linkedInError && (
              <p className="mt-1 text-xs text-destructive">{linkedInError}</p>
            )}
          </div>
        </div>

        <div>
          <StepCardLabel>Pitch deck (optional)</StepCardLabel>
          <AnimatePresence mode="wait" initial={false}>
            {file ? (
              <motion.div
                key="file"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={SPRING}
                className="flex items-center gap-2.5 rounded-lg border border-border/70 bg-background px-3 py-2"
              >
                <FileText className="h-4 w-4 shrink-0 text-primary" />
                <span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  aria-label="Remove file"
                  disabled={isSubmitting}
                  className="rounded p-1 text-muted-foreground/60 transition-colors hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </motion.div>
            ) : (
              <motion.label
                key="dropzone"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                whileHover={shouldReduce ? undefined : { scale: 1.005 }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  pickFile(e.dataTransfer.files?.[0]);
                }}
                className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border py-3 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
              >
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt"
                  disabled={isSubmitting}
                  onChange={(e) => pickFile(e.target.files?.[0] ?? undefined)}
                />
                <Upload className="h-3.5 w-3.5" />
                Drop a deck here — I'll read it and start the analysis
              </motion.label>
            )}
          </AnimatePresence>
          {fileError && <p className="mt-1 text-xs text-destructive">{fileError}</p>}
        </div>

        <AnimatePresence>
          {isSubmitting && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-1.5 overflow-hidden"
            >
              <Progress value={progress} className="h-1.5" />
              <p className="text-xs text-muted-foreground">{progressLabel}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onSkip}
            disabled={isSubmitting}
            className="text-xs text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
          >
            Skip — I'll add one later
          </button>

          <Button
            onClick={() =>
              onSubmit({
                companyName: companyName.trim(),
                website: website.trim(),
                linkedIn: linkedIn.trim(),
                file,
              })
            }
            disabled={!canSubmit}
            className="gap-2"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Add deal
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>

    </StepCard>
  );
}

/** Ref-forwarded for AnimatePresence's `popLayout` measurement. */
const SuggestionRow = forwardRef<
  HTMLButtonElement,
  {
    suggestion: Suggestion;
    icon: ReactNode;
    onSelect: () => void;
    shouldReduce: boolean | null;
  }
>(function SuggestionRow({ suggestion, icon, onSelect, shouldReduce }, ref) {
  return (
    <motion.button
      ref={ref}
      type="button"
      layout
      variants={motionSafe(rowVariants, shouldReduce)}
      exit="exit"
      whileHover={shouldReduce ? undefined : { x: 3 }}
      onClick={onSelect}
      className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-background"
    >
      {suggestion.favicon ? (
        <img
          src={suggestion.favicon}
          alt=""
          className="h-5 w-5 shrink-0 rounded-full border bg-white object-contain p-0.5"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border bg-white">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium">
          {suggestion.title || suggestion.url}
        </span>
        <span className="block truncate text-[11px] text-muted-foreground">
          {suggestion.url}
        </span>
      </span>
    </motion.button>
  );
});

/**
 * What adding a deal sets in motion, filling in as each piece is found — so
 * the user sees the payoff of a name before they've finished typing it.
 */
function DealPreview({
  hasName,
  hasWebsite,
  hasLinkedIn,
  isSubmitting,
}: {
  hasName: boolean;
  hasWebsite: boolean;
  hasLinkedIn: boolean;
  isSubmitting: boolean;
}) {
  const stages = [
    { label: "Website", icon: <Globe className="h-3.5 w-3.5" />, done: hasWebsite },
    { label: "LinkedIn", icon: <Linkedin className="h-3.5 w-3.5" />, done: hasLinkedIn },
    { label: "Full analysis", icon: <FileText className="h-3.5 w-3.5" />, done: false },
  ];

  return (
    <div className="mt-3 space-y-2">
      <ol className="flex items-center gap-2" aria-label="What happens when you add this deal">
        {stages.map((stage, i) => (
          <li key={stage.label} className="flex items-center gap-2">
            {i > 0 && <span className="h-px w-5 bg-border" aria-hidden />}
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors",
                stage.done
                  ? "border-primary/30 bg-primary/5 text-primary"
                  : isSubmitting && i === stages.length - 1
                    ? "border-primary/30 text-primary"
                    : "border-border text-muted-foreground"
              )}
            >
              {stage.done ? <Check className="h-3.5 w-3.5" /> : stage.icon}
              {stage.label}
            </span>
          </li>
        ))}
      </ol>
      <p className="text-[13px] leading-snug text-muted-foreground">
        {hasName
          ? "Pick a match below, or paste the links yourself. The analysis starts as soon as you add it."
          : "Use a company you're actually looking at. It becomes your first full report."}
      </p>
    </div>
  );
}
